import { toJsDate } from "../utils/toJsDate";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useActivityActions } from "../contexts/ActivityContext";
import { ActivityCategory, ParsedAIResponse, User } from "../types";
import { CATEGORIES, getValidCategory } from "../constants";
import {
  ArrowLeftIcon,
  CameraIcon,
  XIcon,
} from "../components/icons/FeatherIcons";
import {
  getPlaceDetails,
  loadGoogleMapsApi,
} from "../services/locationService";
import { getActivityImageUrl } from "../services/imageService";
import { detectActivityCategory } from "../utils/categoryDetection";
import LocationAutocomplete from "../components/LocationAutocomplete";
import ActivityImageSelectionModal from "../components/ActivityImageSelectionModal";
import { useToast } from "../contexts/ToastContext";
import { usePageTracking } from "../hooks/usePageTracking";
import { createPortal } from "react-dom";
import { crashlytics } from "../services/firebase";
import { fallbackImage } from "../utils/getCachedImage";
import { useAuth } from "../contexts/AuthContext";

interface PortalProps {
  children: React.ReactNode;
}

const Portal: React.FC<PortalProps> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(children, document.body) : null;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

const ManualCreatePage: React.FC = () => {
  usePageTracking("ManualCreate", "ManualCreatePage");
  const navigate = useNavigate();
  const location = useLocation();
  const { createActivity, refreshActivities, inviteUsersToActivity } =
    useActivityActions();
  const { currentUser, searchUsers, getUsersByIds } = useAuth();
  const { addToast } = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ActivityCategory>(
    ActivityCategory.Dining,
  );
  const [dateTime, setDateTime] = useState("");
  const [groupSize, setGroupSize] = useState("2");
  const [hours, setHours] = useState("1");
  const [minutes, setMinutes] = useState("0");
  const [isLoading, setIsLoading] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);

  const [selectedPlace, setSelectedPlace] = useState<{
    description: string;
    placeId: string;
  } | null>(null);
  const [initialLocationValue, setInitialLocationValue] = useState("");
  const [keyboardClosing, setKeyboardClosing] = useState(false);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [inviteeIds, setInviteeIds] = useState<string[]>([]);
  const [inviteeUsers, setInviteeUsers] = useState<User[]>([]);
  const [showManageInviteesModal, setShowManageInviteesModal] = useState(false);
  const [inviteeSearchQuery, setInviteeSearchQuery] = useState("");
  const [inviteeSearchResults, setInviteeSearchResults] = useState<User[]>([]);
  const [inviteeSearchIsLoading, setInviteeSearchIsLoading] = useState(false);
  const [inviteeSearchIsLoadingMore, setInviteeSearchIsLoadingMore] =
    useState(false);
  const [inviteeSearchHasMore, setInviteeSearchHasMore] = useState(true);
  const [inviteeSearchLastDoc, setInviteeSearchLastDoc] = useState<any>(null);
  const [inviteeSearchTimeoutId, setInviteeSearchTimeoutId] =
    useState<ReturnType<typeof setTimeout> | null>(null);
  const inviteeLoadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Get current datetime string for min attribute (prevents past date selection)
  const getCurrentDateTimeString = () => {
    const now = new Date();
    // Add 1 minute buffer to account for processing time
    now.setMinutes(now.getMinutes() + 1);
    return now.toISOString().slice(0, 16);
  };

  const [minDateTime, setMinDateTime] = useState(getCurrentDateTimeString());

  // Update minimum datetime every minute to keep it current
  useEffect(() => {
    const interval = setInterval(() => {
      setMinDateTime(getCurrentDateTimeString());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadGoogleMapsApi(["places"])
      .then(() => setIsApiLoaded(true))
      .catch((err) => {
        crashlytics.logError(
          `Error loading Google Maps API: ${JSON.stringify(err)}`,
          "ManualCreatePage.tsx",
          err,
        );
        addToast(
          "Could not load location services. Please try again later.",
          "error",
        );
      });
  }, [addToast]);

  // Handle keyboard visibility detection on mobile
  useEffect(() => {
    const handleResize = () => {
      // Detect if keyboard is likely closing based on viewport height change
      const viewportHeight =
        window.visualViewport?.height || window.innerHeight;
      const screenHeight = screen.height;
      const threshold = screenHeight * 0.75;

      const keyboardOpen = viewportHeight < threshold;

      if (!keyboardOpen && keyboardClosing) {
        // Keyboard just closed, add delay to prevent accidental clicks
        setTimeout(() => setKeyboardClosing(false), 300);
      } else if (keyboardOpen) {
        setKeyboardClosing(false);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    } else {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      } else {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [keyboardClosing]);

  useEffect(() => {
    const { state } = location;
    if (state && state.initialData) {
      const data = state.initialData as ParsedAIResponse;
      if (data.title) setTitle(data.title);
      if (data.description) setDescription(data.description);
      if (data.category) setCategory(data.category as ActivityCategory);
      if (data.groupSize) setGroupSize(String(data.groupSize));
      if (data.isPrivate !== undefined) setIsPrivate(Boolean(data.isPrivate));

      if (data.suggestedDateTime) {
        const date = toJsDate(data.suggestedDateTime);
        const localDateTime = new Date(
          date.getTime() - date.getTimezoneOffset() * 60000,
        )
          .toISOString()
          .slice(0, 16);
        setDateTime(localDateTime);
      }

      if (data.duration) {
        const durationMinutes =
          typeof data.duration === "string"
            ? parseInt(data.duration) || 0
            : data.duration;
        setHours(String(Math.floor(durationMinutes / 60)));
        setMinutes(String(durationMinutes % 60));
      }

      if (data.location && data.placeId) {
        setInitialLocationValue(data.location);
        setSelectedPlace({ description: data.location, placeId: data.placeId });
      }
    }
    if (state?.proposalInvitees && Array.isArray(state.proposalInvitees)) {
      setInviteeIds(state.proposalInvitees as string[]);
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;

    if (!inviteeIds.length || !getUsersByIds) {
      setInviteeUsers([]);
      return undefined;
    }

    const loadInvitees = async () => {
      try {
        const users = await getUsersByIds(inviteeIds);
        if (!cancelled) {
          setInviteeUsers(users);
        }
      } catch (error) {
        crashlytics.logError(
          `Error loading manual invitees: ${getErrorMessage(error)}`,
          "ManualCreatePage.tsx",
          error,
        );
      }
    };

    loadInvitees();

    return () => {
      cancelled = true;
    };
  }, [inviteeIds, getUsersByIds]);

  const handleRemoveInvitee = useCallback((userId: string) => {
    setInviteeIds((prev) => prev.filter((id) => id !== userId));
    setInviteeUsers((prev) => prev.filter((user) => user.id !== userId));
  }, []);

  const handleAddInvitee = useCallback((user: User) => {
    setInviteeIds((prev) =>
      prev.includes(user.id) ? prev : [...prev, user.id],
    );
    setInviteeUsers((prev) =>
      prev.some((existing) => existing.id === user.id) ? prev : [...prev, user],
    );
    setInviteeSearchResults((prev) =>
      prev.filter((result) => result.id !== user.id),
    );
  }, []);

  const resetInviteeSearchState = useCallback(() => {
    setInviteeSearchQuery("");
    setInviteeSearchResults([]);
    setInviteeSearchLastDoc(null);
    setInviteeSearchHasMore(true);
    setInviteeSearchIsLoading(false);
    setInviteeSearchIsLoadingMore(false);
  }, []);

  const filterInviteeSearchUsers = useCallback(
    (users: User[]) => {
      return users.filter(
        (user) => user.id !== currentUser?.id && !inviteeIds.includes(user.id),
      );
    },
    [currentUser?.id, inviteeIds],
  );

  const fetchInviteeSearch = useCallback(
    async (query: string, reset: boolean, lastDoc?: any) => {
      if (!currentUser || !searchUsers) return;

      if (reset) {
        setInviteeSearchIsLoading(true);
        setInviteeSearchLastDoc(null);
        setInviteeSearchHasMore(true);
      } else {
        setInviteeSearchIsLoadingMore(true);
      }

      try {
        const result = await searchUsers(query, reset ? null : lastDoc, 20);
        const filteredUsers = filterInviteeSearchUsers(result.users);
        if (reset) {
          setInviteeSearchResults(filteredUsers);
        } else {
          setInviteeSearchResults((prev) => {
            const existingIds = new Set(prev.map((user) => user.id));
            const newUsers = filteredUsers.filter(
              (user) => !existingIds.has(user.id),
            );
            return [...prev, ...newUsers];
          });
        }
        setInviteeSearchLastDoc(result.lastDoc);
        setInviteeSearchHasMore(result.hasMore);
      } catch (error) {
        crashlytics.logError(
          `Error searching manual invitees: ${getErrorMessage(error)}`,
          "ManualCreatePage.tsx",
          error,
        );
        if (reset) {
          setInviteeSearchResults([]);
          setInviteeSearchHasMore(false);
        }
      } finally {
        setInviteeSearchIsLoading(false);
        setInviteeSearchIsLoadingMore(false);
      }
    },
    [currentUser, searchUsers, filterInviteeSearchUsers],
  );

  useEffect(() => {
    if (!showManageInviteesModal) return;

    if (inviteeSearchTimeoutId) {
      clearTimeout(inviteeSearchTimeoutId);
    }

    const timeoutId = setTimeout(() => {
      fetchInviteeSearch(inviteeSearchQuery.trim(), true, null);
    }, 300);

    setInviteeSearchTimeoutId(timeoutId);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [inviteeSearchQuery, showManageInviteesModal, fetchInviteeSearch]);

  useEffect(() => {
    if (!showManageInviteesModal) {
      resetInviteeSearchState();
    }
  }, [showManageInviteesModal, resetInviteeSearchState]);

  const loadMoreInviteeSearch = useCallback(() => {
    if (
      !showManageInviteesModal ||
      inviteeSearchIsLoadingMore ||
      inviteeSearchIsLoading ||
      !inviteeSearchHasMore ||
      !inviteeSearchLastDoc
    )
      return;
    fetchInviteeSearch(inviteeSearchQuery.trim(), false, inviteeSearchLastDoc);
  }, [
    showManageInviteesModal,
    inviteeSearchIsLoadingMore,
    inviteeSearchIsLoading,
    inviteeSearchHasMore,
    inviteeSearchLastDoc,
    fetchInviteeSearch,
    inviteeSearchQuery,
  ]);

  const handleSearchInvitees = useCallback((query: string) => {
    setInviteeSearchQuery(query);
  }, []);

  useEffect(() => {
    if (!showManageInviteesModal) return;
    const trigger = inviteeLoadMoreTriggerRef.current;
    if (!trigger) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          inviteeSearchHasMore &&
          !inviteeSearchIsLoadingMore &&
          !inviteeSearchIsLoading
        ) {
          loadMoreInviteeSearch();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [
    showManageInviteesModal,
    inviteeSearchHasMore,
    inviteeSearchIsLoadingMore,
    inviteeSearchIsLoading,
    loadMoreInviteeSearch,
  ]);

  // Auto-detect category when title, description, or location changes
  useEffect(() => {
    if (title.length > 5) {
      // Only detect when we have meaningful input
      const detectedCategory = detectActivityCategory(
        title,
        description,
        selectedPlace?.description || "",
      );

      // Only auto-update if we don't have a user-selected category yet
      // or if the current category is the default
      if (category === ActivityCategory.Games || !title) {
        // Auto-detecting category
        setCategory(detectedCategory);
      }
    }
  }, [title, description, selectedPlace?.description]);

  // Auto-generate relevant image when category or title changes
  useEffect(() => {
    if (category && !selectedImageUrl) {
      getActivityImageUrl(category, title, description)
        .then((url) => setSelectedImageUrl(url))
        .catch((error) => {
          console.warn("Failed to auto-generate image:", error);
        });
    }
  }, [category, title, description, selectedImageUrl]);

  const handleBackToAI = useCallback(() => {
    const totalDuration =
      (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);

    const prefilledData: Partial<ParsedAIResponse> = {
      title: title || undefined,
      description: description || undefined,
      category: category || undefined,
      suggestedDateTime: dateTime
        ? new Date(dateTime).toISOString()
        : undefined,
      duration: totalDuration >= 15 ? totalDuration : undefined,
      groupSize:
        groupSize && parseInt(groupSize) >= 2 ? parseInt(groupSize) : 2,
      location: selectedPlace?.description || initialLocationValue || undefined,
      placeId: selectedPlace?.placeId || undefined,
      isPrivate: isPrivate,
    };

    // Navigate back to AI create page with state
    navigate("/create", {
      state: { prefilledData, proposalInvitees: inviteeIds },
      replace: true,
    });
  }, [
    navigate,
    title,
    description,
    category,
    dateTime,
    hours,
    minutes,
    groupSize,
    selectedPlace,
    initialLocationValue,
    isPrivate,
    inviteeIds,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !dateTime) {
      addToast("Please fill out Title and Date & Time.", "error");
      return;
    }

    const selectedDateTime = toJsDate(dateTime);
    if (isNaN(selectedDateTime.getTime())) {
      addToast("Please select a valid date and time.", "error");
      return;
    }

    // Enhanced validation for date/time selection
    const now = new Date();
    const oneMinuteFromNow = new Date(now.getTime() + 60000); // 1 minute buffer
    const oneYearFromNow = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year max

    if (selectedDateTime < oneMinuteFromNow) {
      addToast(
        "Activity must be scheduled at least 1 minute in the future.",
        "error",
      );
      return;
    }

    if (selectedDateTime > oneYearFromNow) {
      addToast(
        "Activity cannot be scheduled more than 1 year in advance.",
        "error",
      );
      return;
    }

    // Check if the selected time is during reasonable hours (optional warning)
    const hour = selectedDateTime.getHours();
    if (hour < 6 || hour > 23) {
      // Just a warning, don't block creation
    }
    if (!selectedPlace) {
      addToast("Please select a valid location from the suggestions.", "error");
      return;
    }

    const totalDuration =
      (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    if (totalDuration < 15) {
      addToast("Duration must be at least 15 minutes.", "error");
      return;
    }

    const groupSizeNum = parseInt(groupSize) || 0;
    if (groupSizeNum < 2) {
      addToast("Group size must be at least 2 people.", "error");
      return;
    }

    if (!currentUser) {
      addToast("Please sign in to create an activity.", "error");
      return;
    }

    setIsLoading(true);

    try {
      const locationResult = await getPlaceDetails(selectedPlace.placeId);

      const newActivityId = await createActivity({
        title,
        description: description || "",
        category: getValidCategory(category),
        location: locationResult.formattedAddress || selectedPlace.description,
        coordinates: locationResult.coordinates,
        dateTime: new Date(dateTime).toISOString(),
        duration: totalDuration,
        groupSize: parseInt(groupSize) || 2,
        imageUrl: selectedImageUrl || undefined,
        creatorId: currentUser.id,
        isPrivate: isPrivate,
      });

      if (newActivityId) {
        setIsLoading(false);
        addToast("🎉 Activity posted successfully!", "success");

        try {
          navigate(`/activity/${newActivityId}`);
        } catch (navigationError) {
          crashlytics.logError(
            `Error during navigation: ${String(navigationError)}`,
            "ManualCreatePage.tsx",
            navigationError,
          );
          window.location.href = `/activity/${newActivityId}`;
        }

        void (async () => {
          try {
            const invitees = inviteeIds.filter((id) => id !== currentUser?.id);
            const backgroundTasks: Promise<any>[] = [refreshActivities()];
            if (invitees.length > 0) {
              backgroundTasks.push(inviteUsersToActivity(newActivityId, invitees));
            }
            await Promise.all(backgroundTasks);
          } catch (backgroundError) {
            crashlytics.logError(
              `Error in post-create background sync: ${String(backgroundError)}`,
              "ManualCreatePage.tsx",
              backgroundError,
            );
          }
        })();
      } else {
        crashlytics.logError(
          `Post Flow: Activity creation returned null/undefined`,
          "ManualCreatePage.tsx",
          new Error("Activity creation returned null/undefined"),
        );
        setIsLoading(false);
        throw new Error("Failed to create activity. Please try again.");
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "An unknown error occurred.";
      addToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const hasInviteeSearchQuery = inviteeSearchQuery.trim().length > 0;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Responsive container: mobile-first with larger breakpoints */}
      <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto h-full flex flex-col">
        <header className="flex-shrink-0 bg-white px-3 md:px-6 pb-3 pt-3 flex items-center shadow-sm border-b border-gray-200 sticky-header">
          <button
            onClick={handleBackToAI}
            className="text-gray-500 mr-4 p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <h1 className="text-lg md:text-xl font-bold text-gray-900">
            Create Manually
          </h1>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 hide-scrollbar page-content-top">
          <form
            onSubmit={handleSubmit}
            className="space-y-5 bg-white p-4 md:p-6 lg:p-8 rounded-lg border border-gray-200 max-w-2xl mx-auto"
          >
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Activity Title
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Evening Coffee & Chat"
                className="w-full bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 border border-gray-300"
                required
                maxLength={100}
              />
              <p className="text-right text-xs text-gray-400 mt-1">
                {title.length}/100
              </p>
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Tell us a bit more about the plan..."
                className="w-full bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 border border-gray-300"
                maxLength={500}
              />
              <p className="text-right text-xs text-gray-400 mt-1">
                {description.length}/500
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports rich text: *bold*, _italic_, ~strike~, `code`, and links.
              </p>
            </div>

            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Category
                {title.length > 5 && (
                  <span className="ml-2 text-xs text-primary">
                    ✨ Auto-detected
                  </span>
                )}
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) =>
                  setCategory(e.target.value as ActivityCategory)
                }
                className="w-full bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 border border-gray-300 appearance-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="dateTime"
                  className="block text-sm font-medium text-gray-600 mb-1"
                >
                  Date & Time *
                </label>
                <input
                  id="dateTime"
                  type="datetime-local"
                  value={dateTime}
                  min={minDateTime}
                  onChange={(e) => setDateTime(e.target.value)}
                  className="w-full bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 border border-gray-300"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Select when you'd like this activity to take place (must be in
                  the future)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Duration
                </label>

                {/* Quick Duration Presets */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { label: "30min", hours: "0", minutes: "30" },
                    { label: "1hr", hours: "1", minutes: "0" },
                    { label: "1.5hrs", hours: "1", minutes: "30" },
                    { label: "2hrs", hours: "2", minutes: "0" },
                    { label: "3hrs", hours: "3", minutes: "0" },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => {
                        setHours(preset.hours);
                        setMinutes(preset.minutes);
                      }}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        hours === preset.hours && minutes === preset.minutes
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center space-x-2">
                  <div className="relative flex-1">
                    <input
                      id="hours"
                      type="number"
                      min="0"
                      max="12"
                      value={hours}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string, then validate and constrain
                        if (value === "") {
                          setHours("");
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            setHours(
                              String(Math.max(0, Math.min(12, numValue))),
                            );
                          }
                        }
                      }}
                      className="w-full bg-gray-100 rounded-lg pr-12 pl-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 border border-gray-300"
                      placeholder="1"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                      hr
                    </span>
                  </div>
                  <div className="relative flex-1">
                    <input
                      id="minutes"
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={minutes}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string, then validate and constrain
                        if (value === "") {
                          setMinutes("");
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            // Allow any minute value from 0-59
                            setMinutes(
                              String(Math.max(0, Math.min(59, numValue))),
                            );
                          }
                        }
                      }}
                      className="w-full bg-gray-100 rounded-lg pr-12 pl-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 border border-gray-300"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                      min
                    </span>
                  </div>
                </div>

                {/* Show total duration with better formatting */}
                {(() => {
                  const totalMinutes =
                    (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
                  if (totalMinutes === 0) {
                    return (
                      <p className="text-xs text-gray-400 mt-1">
                        Enter a duration for your activity
                      </p>
                    );
                  }
                  if (totalMinutes < 15) {
                    return (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠️ Minimum duration is 15 minutes
                      </p>
                    );
                  }
                  const displayHours = Math.floor(totalMinutes / 60);
                  const displayMins = totalMinutes % 60;
                  let display = "";
                  if (displayHours > 0) display += `${displayHours}h `;
                  if (displayMins > 0) display += `${displayMins}m`;
                  return (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Duration: {display.trim()}
                    </p>
                  );
                })()}
              </div>
            </div>

            <div>
              {isApiLoaded ? (
                <LocationAutocomplete
                  id="location"
                  label="Location"
                  initialValue={initialLocationValue}
                  onLocationSelect={setSelectedPlace}
                />
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="Loading location search..."
                    className="w-full bg-gray-200 rounded-lg px-4 py-2.5 cursor-wait"
                    disabled
                  />
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="groupSize"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Group Size
              </label>

              {/* Quick Group Size Presets */}
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { label: "2 people", value: "2" },
                  { label: "3-4 people", value: "4" },
                  { label: "5-6 people", value: "6" },
                  { label: "8-10 people", value: "10" },
                  { label: "15+ people", value: "15" },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => setGroupSize(preset.value)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      groupSize === preset.value
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <input
                id="groupSize"
                type="number"
                min="2"
                max="50"
                step="1"
                value={groupSize}
                onFocus={(e) => {
                  // Select all text when user focuses on the input for easy editing
                  e.target.select();
                }}
                onChange={(e) => {
                  setGroupSize(e.target.value);
                }}
                onBlur={(e) => {
                  // Ensure minimum value when user leaves the field
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || value < 2) {
                    setGroupSize("2");
                  }
                }}
                className="w-full bg-gray-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 border border-gray-300"
                placeholder="2"
                required
              />
              {(() => {
                const size = parseInt(groupSize) || 0;
                if (size === 0 || groupSize === "") {
                  return (
                    <p className="text-xs text-gray-400 mt-1">
                      Enter number of people joining
                    </p>
                  );
                }
                if (size < 2) {
                  return (
                    <p className="text-xs text-red-500 mt-1">
                      ⚠️ Minimum 2 people required
                    </p>
                  );
                }
                return (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Group size: {size} people
                  </p>
                );
              })()}
            </div>

            {/* Privacy Toggle */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex-1">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900">
                    Private Activity
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {isPrivate
                    ? "Only visible via link. You can invite specific users."
                    : "Visible to everyone on home feed and map."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsPrivate(!isPrivate)}
                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  isPrivate ? "bg-primary" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                    isPrivate ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  Invite people (optional)
                </h3>
                <button
                  type="button"
                  onClick={() => setShowManageInviteesModal(true)}
                  className="text-xs px-2 py-1 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                >
                  Invite
                </button>
              </div>
              {inviteeUsers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {inviteeUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border border-gray-200 text-sm"
                    >
                      <img
                        src={
                          user.profileImageUrl ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || "User")}&background=random`
                        }
                        alt={user.username}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-gray-700">{user.username}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveInvitee(user.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Image Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Activity Photo
              </label>
              <div className="flex items-center space-x-3">
                {selectedImageUrl && (
                  <div className="w-20 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={selectedImageUrl}
                      alt="Selected activity photo"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.currentTarget.src !== fallbackImage) {
                          e.currentTarget.src = fallbackImage;
                        }
                      }}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => setShowImageModal(true)}
                    className="w-full bg-gray-100 text-gray-700 font-medium py-2.5 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2 border border-gray-300"
                  >
                    <CameraIcon className="w-4 h-4" />
                    <span>
                      {selectedImageUrl ? "Change Photo" : "Choose Photo"}
                    </span>
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    We'll automatically suggest relevant photos based on your
                    activity
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-2 pb-20">
              <button
                type="submit"
                disabled={isLoading || !isApiLoaded || keyboardClosing}
                onTouchEnd={(e) => {
                  if (keyboardClosing) {
                    e.preventDefault();
                    return;
                  }
                  // Add small delay to ensure keyboard transition is complete
                  setKeyboardClosing(true);
                  setTimeout(() => {
                    setKeyboardClosing(false);
                    handleSubmit(e);
                  }, 100);
                }}
                onClick={handleBackToAI}
                className={`w-full bg-primary text-white font-bold py-3 px-4 rounded-full disabled:opacity-50 transition-all duration-300 shadow-lg shadow-primary/30 ${
                  keyboardClosing
                    ? "pointer-events-none"
                    : "hover:opacity-90 transform hover:scale-105"
                }`}
              >
                {isLoading || keyboardClosing
                  ? "Creating..."
                  : "Create Activity"}
              </button>
            </div>
          </form>
        </main>

        {/* Image Selection Modal */}
        <Portal>
          <ActivityImageSelectionModal
            isOpen={showImageModal}
            onClose={() => setShowImageModal(false)}
            onSelect={(imageUrl) => setSelectedImageUrl(imageUrl)}
            category={category}
            title={title}
            description={description}
            location={selectedPlace?.description || initialLocationValue}
            currentImageUrl={selectedImageUrl}
          />
        </Portal>

        <Portal>
          {showManageInviteesModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
              <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col shadow-xl">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Add Invitees
                  </h2>
                  <button
                    onClick={() => {
                      setShowManageInviteesModal(false);
                      setInviteeSearchQuery("");
                      setInviteeSearchResults([]);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <input
                    type="text"
                    placeholder="Search users to invite..."
                    value={inviteeSearchQuery}
                    onChange={(e) => handleSearchInvitees(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />

                  {inviteeSearchIsLoading && (
                    <p className="text-sm text-gray-500 py-3 text-center">
                      Loading users...
                    </p>
                  )}

                  {inviteeUsers.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Invited ({inviteeUsers.length})
                      </h3>
                      <div className="space-y-2">
                        {inviteeUsers.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-100"
                          >
                            <div className="flex items-center gap-2">
                              <img
                                src={
                                  user.profileImageUrl ||
                                  `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || "User")}&background=random`
                                }
                                alt={user.username}
                                className="w-8 h-8 rounded-full"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {user.username}
                                </p>
                                {user.location && (
                                  <p className="text-xs text-gray-600">
                                    {user.location}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveInvitee(user.id)}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      {hasInviteeSearchQuery ? "Search Results" : "Suggested"}
                    </h3>
                    {inviteeSearchResults.length > 0 ? (
                      <div className="space-y-2">
                        {inviteeSearchResults.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg border border-gray-100 cursor-pointer"
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <img
                                src={
                                  user.profileImageUrl ||
                                  `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || "User")}&background=random`
                                }
                                alt={user.username}
                                className="w-8 h-8 rounded-full"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {user.username}
                                </p>
                                {user.location && (
                                  <p className="text-xs text-gray-600">
                                    {user.location}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleAddInvitee(user)}
                              className="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                            >
                              Add
                            </button>
                          </div>
                        ))}
                        {inviteeSearchHasMore && (
                          <div
                            ref={inviteeLoadMoreTriggerRef}
                            className="h-8"
                          />
                        )}
                        {inviteeSearchIsLoadingMore && (
                          <p className="text-xs text-gray-500 text-center py-2">
                            Loading more...
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 py-4 text-center">
                        {hasInviteeSearchQuery
                          ? "No users found"
                          : "Type a username to search for people to invite"}
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 p-4 sticky bottom-0 bg-white">
                  <button
                    onClick={() => {
                      setShowManageInviteesModal(false);
                      setInviteeSearchQuery("");
                      setInviteeSearchResults([]);
                    }}
                    className="w-full py-2 px-4 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </Portal>
      </div>
      {/* End of responsive container */}
    </div>
  );
};

export default ManualCreatePage;
